/**
 * Super Admin console (platform admin host) — /api/platform/*.
 *
 * Every entry hits routes/platform.js: operations real since D8a, business and
 * admin since D8b. Routes check the admin's permissions (lib/platform/permissions.js).
 */

const platform = (path, method = 'GET', data) => ({ url: `/api/platform${path}`, method, data });

export default {
  // --- real ---------------------------------------------------------------
  platformLogin: (data) => platform('/login', 'POST', data),
  platformLogout: () => platform('/logout', 'POST'),
  platformMe: () => platform('/me'),
  platformStats: () => platform('/stats'),
  platformPlans: () => platform('/plans'),
  // Query string in the URL: useApi forwards only url/method/data.
  platformAvailability: ({ slug, domain }) => {
    const qs = new URLSearchParams();
    if (slug) qs.set('slug', slug);
    if (domain) qs.set('domain', domain);
    return platform(`/availability?${qs}`);
  },
  platformMerchants: () => platform('/merchants'),
  platformMerchant: ({ id }) => platform(`/merchants/${id}`),
  platformCreateMerchant: (data) => platform('/merchants', 'POST', data),
  platformRetry: ({ id }) => platform(`/merchants/${id}/retry`, 'POST'),
  platformSuspend: ({ id }) => platform(`/merchants/${id}/suspend`, 'POST'),
  platformResume: ({ id }) => platform(`/merchants/${id}/resume`, 'POST'),
  platformVerifyDomain: ({ id, domainId }) => platform(`/merchants/${id}/domains/${domainId}/verify`, 'POST'),

  // --- real since D8a -------------------------------------------------------
  // ADMIN-01 seller onboarding / KYC (reviewer). Document files: GET /onboarding/:id/documents/:type/file
  platformOnboarding: () => platform('/onboarding'),
  platformOnboardingApp: ({ id }) => platform(`/onboarding/${id}`),
  platformOnboardingDecide: ({ id, action, reason }) => platform(`/onboarding/${id}/decision`, 'POST', { action, reason }),
  platformOnboardingDocument: ({ id, type, status, note }) => platform(`/onboarding/${id}/documents/${type}`, 'POST', { status, note }),
  // ADMIN-02 listing moderation (D18)
  platformModeration: () => platform('/moderation'),
  platformModerationDecide: ({ id, action, reason }) => platform(`/moderation/${id}/decision`, 'POST', { action, reason }),
  // ADMIN-03 exception desk (resolutions run in the store's own DB)
  platformExceptions: () => platform('/exceptions'),
  platformExceptionDecide: ({ id, action, reason, assignee, resolution }) => platform(`/exceptions/${id}/decision`, 'POST', { action, reason, assignee, resolution }),
  // ADMIN-04 reconciliation: provider events vs store ledgers for a month (YYYY-MM)
  platformReconciliation: ({ period } = {}) => platform(`/reconciliation${period ? `?period=${period}` : ''}`),
  // Public seller application (D17): no admin session; the status link carries a private token.
  sellerApply: (data) => platform('/apply', 'POST', data),
  platformPublicPlans: () => platform('/apply/plans'),
  // What can be sold (MERCH-89): industries and their product types, for "What do you sell?" and the console.
  platformSellCatalog: () => platform('/apply/catalog'),
  sellerApplication: ({ token }) => platform(`/apply/${token}`),
  sellerApplicationSubmit: ({ token }) => platform(`/apply/${token}/submit`, 'POST'),

  // --- real since D8b --------------------------------------------------------
  // Dashboard: 6-month GMV / orders across stores, billed MRR, health tiles.
  platformOverview: () => platform('/overview'),
  // Audit log (append-only): area = action prefix (merchant, onboarding, policy, billing, admin…).
  platformAudit: ({ q, action, company, limit } = {}) => {
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    if (action && action !== 'all') qs.set('action', action);
    if (company) qs.set('company', company);
    if (limit) qs.set('limit', String(limit));
    return platform(`/audit?${qs}`);
  },
  // One store: database probe history, schema, store counts.
  platformMerchantMetrics: ({ id }) => platform(`/merchants/${id}/metrics`),
  platformMerchantInvoices: ({ id }) => platform(`/merchants/${id}/invoices`),
  // Seller capabilities (D57): vendor / dropshipper, switched on per store.
  platformCapabilities: () => platform('/capabilities'),
  platformMerchantCapabilities: ({ id, capabilities }) => platform(`/merchants/${id}/capabilities`, 'POST', { capabilities }),
  // Approved industries (MERCH-89): a store turns on product types only within them; audited.
  platformMerchantIndustries: ({ id, industries }) => platform(`/merchants/${id}/industries`, 'POST', { industries }),
  // Marketplace toggles (MERCH-89): product types and industries shown in the marketplace; audited.
  platformMarketplace: () => platform('/marketplace'),
  platformMarketplaceSet: ({ kind, key, visible, reason }) => platform('/marketplace', 'POST', { kind, key, visible, reason }),
  // Custom-home kill switch for one store (ADR 0007 §7).
  platformMerchantCustomHome: ({ id, enabled }) => platform(`/merchants/${id}/custom-home`, 'POST', { enabled }),
  // ADMIN-05 plans (versioned), subscriptions, invoices (netted from payouts, D20), policies.
  platformPlanUpdate: ({ key, ...fields }) => platform(`/plans/${key}`, 'PATCH', fields),
  platformPlanHistory: ({ key }) => platform(`/plans/${key}/history`),
  platformSubscriptions: () => platform('/subscriptions'),
  platformInvoices: () => platform('/invoices'),
  platformPolicies: () => platform('/policies'),
  platformPolicyPublish: ({ key, body, note }) => platform(`/policies/${key}/versions`, 'POST', { body, note }),
  // ADMIN-06 risk & operations (storefront beacon D22 + orders, refunds, repairs).
  platformRisk: () => platform('/analytics/risk'),
  // Domains, database probes, provisioning jobs, background job runs.
  platformInfrastructure: () => platform('/infrastructure'),
  // Platform admins: invite by join link, role change, disable / enable.
  // Connected AI assistants (D48): own, or everyone's with admins.manage (scope 'all').
  platformConnections: ({ scope } = {}) => platform(`/connections${scope === 'all' ? '?scope=all' : ''}`),
  platformConnectionDisconnect: ({ id }) => platform(`/connections/${encodeURIComponent(id)}/disconnect`, 'POST', {}),
  platformAdmins: () => platform('/admins'),
  platformAdminInvite: (data) => platform('/admins', 'POST', data),
  platformAdminRole: ({ id, role }) => platform(`/admins/${id}/role`, 'POST', { role }),
  platformAdminStatus: ({ id, status }) => platform(`/admins/${id}/status`, 'POST', { status }),
  // Public: an invited admin claims the account (no session).
  platformJoin: ({ token }) => platform(`/join/${encodeURIComponent(token)}`),
  platformJoinAccept: ({ token, name, password }) => platform(`/join/${encodeURIComponent(token)}`, 'POST', { name, password }),
};
